import { Link, Navigate, useParams, useSearch } from "@tanstack/react-router";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import type { useAuth } from "@clerk/react";
import type { ReactElement } from "react";
import { authenticatedApi } from "@/lib/api";
import { useRdMetrics, type CompactRdMetrics, type MetricsState } from "@/lib/metrics";
import { useFeatures } from "@/lib/use-features";
import { canRenameRd } from "@/lib/permissions";
import { HeadlineStrip } from "@/components/v2/headline-strip";
import type { FunctionalFormLink } from "@/lib/metrics-display";
import { MetricsNotPublished } from "@/components/v2/metrics-not-published";
import { MetricsNotPublishedActions } from "@/components/v2/metrics-not-published-actions";
import { QueryErrorState } from "@/components/v2/query-error-state";
import { RdChatsCardSlot } from "@/components/v2/rd-chats-card-slot";
import {
  SnapshotContextRow,
  type ContextRowState,
  type RenderContextLink,
} from "@/components/v2/snapshot-context-row";
import {
  SnapshotProfileCard,
  SnapshotProfileCardSkeleton,
} from "@/components/v2/snapshot-profile-card";
import type { FormProfileData } from "@/components/v2/forms-profile-card";
import type { MeetingNoteData } from "@/components/v2/meeting-note-types";
import {
  sessionListRefetchInterval,
  type SessionRowData,
} from "@/components/v2/session-row";
import type { FormSnapshotData } from "../../../shared/form-types";
import { FEATURE_FLAGS } from "../../../shared/features";
import { THEMED_QUIET_LINK } from "@/lib/always-look";
import { cn } from "@/lib/utils";
import {
  SurfaceShell,
  SurfaceStatus,
  surfaceHeader,
  useSurfaceBase,
} from "./rd-workspace";

type GetToken = ReturnType<typeof useAuth>["getToken"];

/** Collapse a list query into the context row's three-state shape. */
function rowState<T>(query: UseQueryResult<{ data: T }>): ContextRowState<T> {
  if (query.isPending) return { status: "pending" };

  if (query.isError) return { status: "error" };

  return { status: "ready", data: query.data.data };
}

/**
 * The Snapshot's non-metrics reads: the forms profile plus the sessions,
 * meeting-notes, and forms lists the context row derives from. Query keys
 * match the sibling surfaces so each list loads once per RD.
 */
function useSnapshotRecords(rdId: string, getToken: GetToken) {
  const profile = useQuery({
    queryKey: ["forms-profile", rdId],
    queryFn: () =>
      authenticatedApi<{ data: FormProfileData | null }>(
        getToken,
        `/api/rds/${rdId}/forms/profile`
      ),
  });
  const sessions = useQuery({
    queryKey: ["rd-sessions", rdId],
    queryFn: () =>
      authenticatedApi<{ data: SessionRowData[] }>(
        getToken,
        `/api/rds/${rdId}/sessions`
      ),
    // NO-775: slow re-read while a pasted Session waits for Healthie.
    refetchInterval: (query) => sessionListRefetchInterval(query.state.data?.data),
  });
  const meetingNotes = useQuery({
    queryKey: ["meeting-notes", rdId],
    queryFn: () =>
      authenticatedApi<{ data: MeetingNoteData[] }>(
        getToken,
        `/api/rds/${rdId}/meeting-notes`
      ),
  });
  const forms = useQuery({
    queryKey: ["forms", rdId],
    queryFn: () =>
      authenticatedApi<{ data: FormSnapshotData[] }>(
        getToken,
        `/api/rds/${rdId}/forms`
      ),
  });

  return { profile, sessions, meetingNotes, forms };
}

function contextLinkRenderer(rdId: string): RenderContextLink {
  return (surface, className, children, externalTarget) =>
    typeof surface === "object" ? (
      <Link
        to="/rds/$rdId/sessions/$sessionId"
        params={{ rdId, sessionId: surface.sessionId }}
        className={className}
        {...externalTarget}
      >
        {children}
      </Link>
    ) : (
      <Link to={`/rds/$rdId/${surface}`} params={{ rdId }} className={className}>
        {children}
      </Link>
    );
}

/** The missing-minimum flag's link to this RD's Functional form (NO-667). */
function functionalFormLinkFor(rdSlug: string): FunctionalFormLink {
  return function FunctionalFormLinkTo(label) {
    return (
      <Link
        to="/rds/$rdId/forms"
        params={{ rdId: rdSlug }}
        className={cn(
          "font-medium text-primary underline-offset-4 hover:underline",
          THEMED_QUIET_LINK
        )}
      >
        {label}
      </Link>
    );
  };
}

/**
 * The Profile card's link to set up a missing profile (NO-669). It keeps the
 * Snapshot's own `rdId` param, so the Forms page saves into the same
 * `["forms-profile", rdId]` entry the Snapshot reads on return.
 */
function profileSetupLinkFor(rdId: string): FunctionalFormLink {
  return function ProfileSetupLinkTo(label) {
    return (
      <Link
        to="/rds/$rdId/forms"
        params={{ rdId }}
        search={{ profile: "setup" }}
        className={cn(
          "font-medium text-primary underline-offset-4 hover:underline",
          THEMED_QUIET_LINK
        )}
      >
        {label}
      </Link>
    );
  };
}

/**
 * The Snapshot's metrics region once the disabled and loading states are
 * settled: the headline strip, the not-published state, or the generic failure.
 */
function SnapshotMetrics({
  rdId,
  name,
  metrics,
}: {
  rdId: string;
  name: string;
  metrics: MetricsState<CompactRdMetrics>;
}): ReactElement {
  if (metrics.status === "ready")
    return (
      <HeadlineStrip
        metrics={metrics.data}
        functionalFormLink={functionalFormLinkFor(rdId)}
      />
    );

  if (metrics.status === "not_published") {
    return (
      <MetricsNotPublished
        name={name}
        actions={<MetricsNotPublishedActions rdId={rdId} />}
      />
    );
  }

  if (metrics.status === "error") {
    return (
      <QueryErrorState
        error={metrics.error.cause}
        notFoundText="Metrics aren't available for this RD"
        onRetry={() => metrics.retry?.()}
        className="rounded-xl border border-border bg-background px-[14px] py-3"
      />
    );
  }

  return <SnapshotLoading />;
}

/**
 * The RD Snapshot body: the RD header, the four-cell context row, the
 * read-only contract profile, and the 2x3 headline strip. Mounted only once
 * the supervisor-metrics flag is confirmed on, so no metrics request or tile
 * footprint exists for a flag-off viewer. In the rail frame the Chats card
 * closes the page (NO-754). The chip, target, watch band, fleet
 * figures, and sentence all come from the read contract; the client draws and
 * never grades. The context row reuses the sibling surfaces' query keys, so
 * the sessions, meeting-notes, and forms lists load once per RD.
 */
function SnapshotBody({ rdId }: { rdId: string }): ReactElement {
  const { getToken, rd, me } = useSurfaceBase(rdId);
  const metrics = useRdMetrics(rdId);
  const { profile, sessions, meetingNotes, forms } = useSnapshotRecords(rdId, getToken);
  const renderLink = contextLinkRenderer(rdId);

  // Flag off on the server, or a viewer without a metrics permission: keep
  // today's landing surface.
  if (
    metrics.status === "disabled" ||
    (metrics.status === "error" && metrics.error.kind === "access_denied")
  ) {
    return <Navigate to="/rds/$rdId/scorecards" params={{ rdId }} replace />;
  }

  const detail = rd.data?.data;
  if (!detail) return <SurfaceStatus rd={rd} />;

  if (metrics.status === "loading") return <SnapshotLoading />;

  return (
    <SurfaceShell detail={detail} surfaceLabel="Snapshot">
      {surfaceHeader(rdId, detail, me.data ? canRenameRd(me.data.user) : false)}
      <SnapshotContextRow
        meetingNotes={rowState(meetingNotes)}
        sessions={rowState(sessions)}
        forms={rowState(forms)}
        profile={rowState(profile)}
        sessionQuality={detail.sessionQuality}
        renderLink={renderLink}
      />
      {profile.isError ? (
        <QueryErrorState
          error={profile.error}
          notFoundText="Contract profile isn't available for this RD"
          onRetry={() => void profile.refetch()}
          className="rounded-xl border border-border bg-background px-[14px] py-3"
        />
      ) : profile.isPending ? (
        <SnapshotProfileCardSkeleton />
      ) : (
        <SnapshotProfileCard
          profile={profile.data.data}
          setupLink={profileSetupLinkFor(rdId)}
        />
      )}
      <SnapshotMetrics rdId={rdId} name={detail.name} metrics={metrics} />
      <RdChatsCardSlot rdParam={rdId} rdName={detail.name} surface="overview" />
    </SurfaceShell>
  );
}

/**
 * /rds/$rdId. Legacy ?meetingNoteId deep links still bounce to the Meetings
 * surface. With the supervisor-metrics flag confirmed on, the route is the RD
 * Snapshot; while the flag read is pending nothing metrics-shaped renders, and
 * a flag that is off or unreadable keeps the Session Scorecards landing.
 */
export function RdDetail(): ReactElement {
  const { rdId } = useParams({ strict: false }) as { rdId: string };
  const search = useSearch({ strict: false }) as { meetingNoteId?: string };
  const meetingNoteId =
    typeof search.meetingNoteId === "string" ? search.meetingNoteId : null;
  const features = useFeatures();

  if (meetingNoteId) {
    return (
      <Navigate
        to="/rds/$rdId/meetings"
        params={{ rdId }}
        search={{ meetingNoteId }}
        replace
      />
    );
  }

  if (features.status === "pending") {
    return <SnapshotLoading />;
  }

  if (features.data?.data[FEATURE_FLAGS.supervisorMetrics] !== true) {
    return <Navigate to="/rds/$rdId/scorecards" params={{ rdId }} replace />;
  }

  return <SnapshotBody rdId={rdId} />;
}

function SnapshotLoading(): ReactElement {
  return (
    <div className="flex items-center justify-center h-full">
      <p className="text-muted-foreground">Loading...</p>
    </div>
  );
}
